import { Injectable } from '@angular/core';
import { PlatformService } from './platform.service';
import { RestService } from './rest.service';

export interface TestNotifResult {
  ok: boolean;
  // 'web-shown' | 'web-shown-no-server' | 'sent' | 'permission-<state>' | 'error'
  detail: string;
}

/**
 * Push notifications.
 *  - Native (iOS/Android): real FCM round-trip. Register the device's FCM token
 *    with the server, then the server pushes via firebase-admin.
 *  - Web: the browser Notification API. We still ping the server endpoint for a
 *    full round-trip, but the notification itself is shown locally (no service
 *    worker / FCM web push is configured).
 *
 * Goal / routine reminders are intentionally NOT handled here — those are local
 * (@capacitor/local-notifications) and never go through the server.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  constructor(
    private platform: PlatformService,
    private rest: RestService,
  ) {}

  // ---------------------------------------------------------------- Web ----

  get webSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  /** "Subscribed" on web == the user has granted notification permission. */
  get webSubscribed(): boolean {
    return this.webSupported && Notification.permission === 'granted';
  }

  async ensureWebPermission(): Promise<NotificationPermission> {
    if (!this.webSupported) return 'denied';
    if (Notification.permission === 'default') {
      try {
        return await Notification.requestPermission();
      } catch {
        return 'denied';
      }
    }
    return Notification.permission;
  }

  private showWebNotification(title: string, body: string): void {
    if (!this.webSubscribed) return;
    try {
      // eslint-disable-next-line no-new
      new Notification(title, { body });
    } catch (e) {
      console.error('push: web notification failed:', e);
    }
  }

  // ------------------------------------------------------------- Native ----

  private foregroundWired = false;

  /** Request permission, fetch the FCM token, register it server-side. */
  async registerNative(): Promise<string | null> {
    if (!this.platform.isNative) return null;
    try {
      const { FirebaseMessaging } = await import(
        '@capacitor-firebase/messaging'
      );
      const perm = await FirebaseMessaging.requestPermissions();
      if (perm.receive !== 'granted') return null;
      await this.wireForegroundDisplay();
      const { token } = await FirebaseMessaging.getToken();
      if (token) {
        await this.rest.request({
          path: 'notifications/register-token',
          method: 'POST',
          body: { token, platform: this.platform.platform },
        });
      }
      return token || null;
    } catch (e) {
      console.error('push: native register failed:', e);
      return null;
    }
  }

  /**
   * Foreground delivery: when the app is open, FCM hands the push to JS via
   * `notificationReceived` instead of auto-posting it to the system tray. With
   * no listener the push arrives but is invisible — which made the test button
   * look broken even when the send succeeded. Mirror it to a local notification
   * so it shows whether the app is foreground or background. Registered once.
   */
  private async wireForegroundDisplay(): Promise<void> {
    if (this.foregroundWired) return;
    this.foregroundWired = true;
    try {
      const { FirebaseMessaging } = await import(
        '@capacitor-firebase/messaging'
      );
      const { LocalNotifications } = await import(
        '@capacitor/local-notifications'
      );
      await LocalNotifications.requestPermissions();
      await FirebaseMessaging.addListener('notificationReceived', async (e) => {
        const n: any = e?.notification;
        const title = n?.title || n?.data?.title || '';
        const body = n?.body || n?.data?.body || '';
        if (!title && !body) return;
        try {
          await LocalNotifications.schedule({
            notifications: [
              { id: Math.floor(Date.now() % 2147483647), title, body },
            ],
          });
        } catch (err) {
          console.error('push: foreground local display failed:', err);
        }
      });
    } catch (e) {
      this.foregroundWired = false;
      console.error('push: foreground wiring failed:', e);
    }
  }

  // --------------------------------------------------------------- Test ----

  /** Staging "Test notification" button entry point. */
  async sendTest(title: string, body: string): Promise<TestNotifResult> {
    if (this.platform.isNative) {
      const token = await this.registerNative();
      if (!token) return { ok: false, detail: 'permission-denied' };
      try {
        const resp: any = await this.rest.request({
          path: 'notifications/test',
          method: 'POST',
          body: { title, body },
          throwError: true,
        });
        return {
          ok: (resp?.sent ?? 0) > 0 || resp?.web,
          detail: 'sent',
        };
      } catch (e) {
        console.error('push: native test send failed:', e);
        return { ok: false, detail: 'error' };
      }
    }

    // Web: ensure permission, full server round-trip, then show locally.
    const perm = await this.ensureWebPermission();
    if (perm !== 'granted') return { ok: false, detail: 'permission-' + perm };

    let serverOk = false;
    try {
      const resp: any = await this.rest.request({
        path: 'notifications/test',
        method: 'POST',
        body: { title, body },
        throwError: true,
      });
      serverOk = !!resp;
    } catch (e) {
      console.error('push: web test server call failed:', e);
    }
    this.showWebNotification(title, body);
    return {
      ok: true,
      detail: serverOk ? 'web-shown' : 'web-shown-no-server',
    };
  }
}
